import Store from '../Common/Store';

/**
 * 앱 브릿지 — 웹 ↔ 네이티브 앱(안드로이드·iOS) 통신의 단일 진입점. `$j.F.App`
 *
 * ⚠ `$j.F.Android` 는 이 객체의 **영구 별칭**이다(app.js). 지우지 않는다 — 설치된 앱이
 *   `$j.F.Android.callbackBroker(...)` 같은 호출을 코드에 박아 두고 있고, 옛 버전은 몇 년씩 남는다.
 *   새 코드와 웹이 앱에 넘기는 콜백 이름은 `$j.F.App.*` 를 쓴다.
 *
 * 클래스 이름이 App 이 아닌 이유: 이 파일은 네이티브 전역 `App`(window.App.appBroker)을 부른다.
 * 클래스를 App 으로 지으면 모듈 안에서 그 전역을 가린다.
 */
export default class AppBridge {
    /* =============================================================================
     *  0. 인프라 / 헬퍼
     * =============================================================================
     *  - _apps / _deeplinkContext / _contextByApp : 앱별 웹 진입 계약(G5.APPS — 서버 AppRegistry 가 정본)
     *  - 앱별 부트 흐름(진입 시 무엇을 앱에 보내나)은 여기 두지 않는다 — 장비불러 $j.F.JbApp(/m·/ma), 악덕 $j.F.Devil.App
     *  - isApp / getVersion / getPackage / _getPageCode : 동기 헬퍼
     *  - getVersion·getPackage 는 App.* 동기 반환을 노출 (broker 부적합 메서드)
     * ============================================================================= */

    /**
     * 앱별 웹 진입 계약 — G5.APPS (서버 App\Services\NativeApp\AppRegistry::client(), /asset/bootstrap 이 심는다).
     *   {default:'JB', apps:{JB:{package, home, pages:{code:path}}, MC:{package}, BD:{...}}}
     * 브리지는 앱 코드·경로·페이지코드를 코드로 알지 않고 이 표만 읽는다.
     * bootstrap 을 싣지 않는 화면(사용자PC 등)이면 빈 표 — 판정은 기본 앱, 딥링크는 홈으로 떨어진다.
     */
    _apps() {
        const a = (typeof G5 !== 'undefined' && G5 && G5.APPS) ? G5.APPS : null;
        return { def: (a && a.default) || 'JB', apps: (a && a.apps) || {} };
    }

    /** 앱 코드 → {list: 페이지코드표, home} — home·pages 가 없는 앱이면 null */
    _contextOf(code) {
        const a = this._apps().apps[code];
        return (a && a.home && a.pages) ? { list: a.pages, home: a.home } : null;
    }

    /**
     * 현재 경로가 어느 앱 화면인지로 딥링크 맵·홈 폴백 반환.
     * 같은 main.js 가 여러 앱 웹뷰에서 로드되므로 경로(각 앱의 home 아래인가)로 판별하고, 못 가르면 기본 앱.
     * @returns {{list: Object, home: string}}
     */
    _deeplinkContext() {
        const p = window.location.pathname;
        const { def, apps } = this._apps();
        for (const code of Object.keys(apps)) {
            const home = apps[code].home;
            if (home && (p === home || p.startsWith(home + '/'))) return this._contextOf(code);
        }
        return this._contextOf(def) || { list: {}, home: '/' };
    }

    /**
     * app 파라미터로 딥링크 맵·홈 폴백 반환. 딥링크는 app 을 명시 전달하므로 우선 사용하고,
     * app 미지정(구 호출)이거나 웹 진입 계약이 없는 앱이면 경로 기반(_deeplinkContext)으로 폴백한다.
     * @param {string} [app] 앱 코드 (G5.APPS 의 키)
     * @returns {{list: Object, home: string}}
     */
    _contextByApp(app) {
        const ctx = app ? this._contextOf(String(app).toUpperCase()) : null;
        return ctx || this._deeplinkContext();
    }
    /**
     * 현 URL 의 경로가 (현재 앱의) 페이지코드표에 매핑되어 있으면 해당 코드 반환.
     * 앱에서는 현재 페이지를 하드코딩으로 처리하지말고 이함수를 호출하여 처리하면된다.
     * @returns {string|null}
     */
    _getPageCode() {
        const url = window.location.href;
        // Remove query parameters from URL
        const path = url.split('?')[0];
        // Find matching code for current path (사이트별 맵)
        for (const [code, urlPath] of Object.entries(this._deeplinkContext().list)) {
            if (path.endsWith(urlPath)) {
                return code;
            }
        }
        return null;
    }

    /**
     * 안드로이드 앱에서 실행되는지 여부
     * @returns {boolean}
     */
    /** 대리진입(관리자가 남의 계정) 중인가 — 서버 is_admin_proxy() 를 그대로 받는다 */
    isProxyNow() {
        return (typeof G5 !== 'undefined') && !!G5['isAdminProxy'];
    }

    isApp() {
        // 대리진입(관리자가 남의 계정) 중엔 '앱 아님'으로 취급해 네이티브 기능을 전부 죽인다.
        // 회원 데이터(회원번호·알림음·TTS·진동)가 CS 폰에 심기는 것을 원천 차단하는 대신,
        // 대리 중에는 지도 현위치·페이지코드 통보·웹뷰 하트비트 등 앱 기능이 함께 no-op 이 된다.
        // 예외는 callBroker(ignoreProxy=true) 로 이 게이트를 우회하는 액션뿐 — clearHistory · 이미지업로드 · getCapabilities(fileUploadSupported·originalUploadSupported).
        // 판정은 서버 is_admin_proxy() = G5.isAdminProxy. (.ai/proxy-login.md §4-4)
        if (this.isProxyNow()) return false;
        return !!window.App;
    }

    /**
     * 앱버전을 가져온다. (동기 반환)
     *
     * @returns {number}
     */
    getVersion() {
        if (window.App && typeof App.getAppVersion !== 'undefined') {
            return App.getAppVersion();
        }
        return 0;
    }

    /**
     * 어느 앱으로 실행중인지 패키지명으로 판정 (동기 반환)
     *
     * 서버가 이 값을 g5_member_devices.app_code 로 저장한다 — 앱별 디바이스/푸시토큰이 갈리는 축이다.
     * 패키지명 → 앱 코드는 G5.APPS 표로 찾는다. 구앱(App.getPackageName() 없음)·모르는 패키지는 기본 앱.
     *
     * @returns {string} 앱 코드 (G5.APPS 의 키)
     */
    getPackage() {
        const { def, apps } = this._apps();
        if (this.hasPackage()) {
            const pkg = App.getPackageName();
            const code = Object.keys(apps).find((c) => apps[c].package === pkg);
            if (code) return code;
        }
        return def;
    }

    /**
     * 로그인 페이지 URL — 머큐리(MC) 구앱은 전용 로그인(/mc), 그 밖은 장비불러(/m).
     * 패키지 판정을 이미 여기서 하므로 로그인 URL 도 이 모듈이 안다.
     * (구 public/js/mobile.js 의 전역 getLoginUrl)
     *
     * @returns {string}
     */
    getLoginUrl() {
        return this.getPackage() == 'MC' ? '/mc' : '/m';
    }

    /**
     * 패키지명을 알려주는 앱인지 (구앱·웹 환경은 false)
     *
     * getPackage() 는 모르면 'JB' 로 떨어지므로, **"정말 장비불러"와 "몰라서 JB"를 구별해야 하는
     * 곳**에서는 이걸 먼저 본다. 예: 서버에 app 을 올릴 때 — 모르는데 'JB' 를 보내면 머큐리 구앱의
     * 앱 컨텍스트(쿠키 MC)를 장비불러로 덮어써 버린다.
     *
     * @returns {boolean}
     */
    hasPackage() {
        return !!(window.App && typeof App.getPackageName !== 'undefined');
    }

    /* ── 브로커 디버깅 로그 ──────────────────────────────────────────────────
     * 앱 웹뷰엔 콘솔이 없어서, 브로커가 실제로 오가는지 화면으로 확인하려는 장치다.
     * 표시는 공용 오버레이 $j.Ui.DebugLog 가 맡고(스택·탭시 전체제거·토글), 여기서는
     * 브로커 고유의 내용(액션·페이로드·호출부)만 만든다.
     *   → (초록) 웹→앱 송신  : 모든 callBroker() (= runAppBroker 포함)
     *   ← (파랑) 앱→웹 수신  : 모든 callbackBroker() (§2 응답 + §3 앱 자체 트리거)
     *
     * 기본 OFF. 켜려면 ?debugLog=1 또는 콘솔에서 $j.Ui.DebugLog.enabled = true (세션 유지)
     * ------------------------------------------------------------------------ */

    /** 로그에 찍지 않는 액션 — 2초 주기 하트비트라 화면이 잠기지 않게 제외 */
    static get DBG_MUTE() { return ['hc']; }

    /**
     * 웹→앱 송신 1건 표시 — callBroker 결과까지 함께 (수락/거부, 즉시값 유무).
     * @param {string} action
     * @param {Object|null} data
     * @param {{ok:number, data:Object|null, err:Object|null}} res
     */
    _dbgSend(action, data, res) {
        if (!($j.Ui && $j.Ui.DebugLog && $j.Ui.DebugLog.enabled)) return;   // 꺼져 있으면 요약 비용도 들이지 않는다
        const ok  = (res.ok == 1) ? '수락' : '거부/웹';
        const ret = res.data ? `ret:${this._dbgJson(res.data)}` : (res.err ? `err:${this._dbgJson(res.err)}` : '');
        $j.Ui.DebugLog.push(`→ ${action} ${ok}`,
            [data ? this._dbgJson(data) : '', ret, `from: ${this._callerHint(action)}`],
            { kind: 'send', tag: 'Android' });
    }

    /**
     * 앱→웹 수신 1건 표시.
     * @param {string} action 콜백 함수명 또는 점경로
     * @param {string} rawData 앱이 보낸 JSON 문자열
     */
    _dbgRecv(action, rawData) {
        if (!($j.Ui && $j.Ui.DebugLog && $j.Ui.DebugLog.enabled)) return;   // 꺼져 있으면 요약 비용도 들이지 않는다
        $j.Ui.DebugLog.push(`← ${String(action).replace(/^\$j\.F\.(App|Android)\./, '')}`,
            [this._dbgJson(rawData)], { kind: 'recv', tag: 'Android' });
    }

    /** 페이로드를 한 줄 요약 — 길면 자르고, 토큰류는 앞뒤만 남긴다 */
    _dbgJson(v) {
        let s;
        try {
            s = (typeof v === 'string') ? v : JSON.stringify(v);
        } catch (e) {
            s = String(v);
        }
        if (!s) return '';
        // FCM 토큰 등 긴 값은 앞8…뒤6(길이) 로 축약 — 화면을 다 잡아먹는다
        s = s.replace(/"(token|uToken|mobie_key)":"([^"]{20,})"/g,
            (m, k, t) => `"${k}":"${t.slice(0, 8)}…${t.slice(-6)}(${t.length})"`);
        return (s.length > 300) ? s.slice(0, 300) + '…' : s;
    }

    /**
     * 호출부 위치 힌트 — 스택에서 브로커 인프라 프레임과 도메인 wrapper(=action 동명 함수)를
     * 건너뛴 첫 줄. 즉 "이 브로커를 실제로 부른 코드" 를 가리킨다.
     * @param {string} [action] 건너뛸 wrapper 이름 (보통 action 과 같다)
     */
    _callerHint(action = '') {
        const SKIP = /_callerHint|_dbgSend|callBroker|_callBroker|runAppBroker/;
        try {
            const lines = (new Error()).stack.split('\n').slice(1);
            const wrapper = /^\w+$/.test(action) ? new RegExp(`\\b${action}\\b`) : null;
            const line = lines.find(l => !SKIP.test(l) && !(wrapper && wrapper.test(l)))
                      || lines[lines.length - 1] || '';
            return line.trim().replace(/^at\s+/, '').replace(/https?:\/\/[^/]+/, '').slice(0, 90);
        } catch (e) {
            return '?';
        }
    }

    /* =============================================================================
     *  1. Web → App  ─  broker 송신 (웹이 앱에 요청)
     * =============================================================================
     *  - callBroker / runAppBroker : 송신 인프라
     *      · callBroker    → {ok, data, err} 정규화 객체. 즉시 반환값이 필요한 액션용
     *      · runAppBroker  → boolean (수락 여부만)
     *  - 각 메서드는 runAppBroker(action, data, callback) 형태의 도메인 wrapper 다.
     *    레거시 폴백은 없다 — 앱이 액션을 모르면 그냥 false (구앱 호환 경로는 2026-08 제거).
     *  - callback 인자에 콜백 함수명을 지정하면 §2 의 응답형 콜백으로 회신됨.
     * ============================================================================= */

    /**
     * 브로커 송신 + 반환값 정규화 (모든 송신의 단일 통로).
     *
     * App.appBroker 의 반환은 앱 버전에 따라 두 형태가 존재하며, 이 차이가 곧 신/구앱 판별이다.
     *   - 구앱: `1`(수락) / `0`(미구현·거부) 스칼라. 결과는 항상 callback 으로만 회신.
     *   - 신앱: `{"ok":1,"data":{...},"err":null}` JSON 문자열.
     *           `data` 가 있으면 즉시값, `null` 이면 결과는 callback 으로 회신.
     * 어느 쪽이든 아래 형태로 정규화해 돌려주므로 호출부는 앱 버전을 몰라도 된다.
     *
     * 앱 외(브라우저) 환경·미지원은 조용히 `{ok:0}` — alert 없음 (alert 는 runAppBroker 만).
     *
     * 모든 송신이 여기를 지나므로 디버깅 로그(_dbgSend)도 이 한 곳에서 찍는다.
     *
     * @param {string} action
     * @param {Object|null} data
     * @param {string|null} callback
     * @returns {{ok:number, data:Object|null, err:Object|null}} ok: 1=수락, 0=거부/미지원
     */
    callBroker(action, data = null, callback = null, ignoreProxy = false) {
        const res = this._callBroker(action, data, callback, ignoreProxy);
        if (!AppBridge.DBG_MUTE.includes(action)) this._dbgSend(action, data, res);
        return res;
    }

    /**
     * callBroker 의 실제 구현 (디버깅 로그를 뺀 순수 송신·정규화)
     *
     * ignoreProxy=true 는 대리진입 게이트(isApp)를 우회한다. 회원 데이터를 CS 폰에 심지 않는
     * 액션에만 쓴다 — 현재 clearHistory · upload · getCapabilities(fileUploadSupported 만) 셋뿐이다. (.ai/proxy-login.md §4-4)
     */
    _callBroker(action, data = null, callback = null, ignoreProxy = false) {
        const appReady = ignoreProxy ? !!window.App : this.isApp();
        if (!appReady || typeof window.App.appBroker !== 'function') {
            return { ok: 0, data: null, err: null };
        }

        if (action === 'upload') {
            data = data || {};
            if (!data.limit) data.limit = 1;
        }

        // version 은 "요청 스키마" 버전 — 응답 형식 협상용이 아니다(응답 형식은 반환값 모양으로 판별).
        // data 필드 규격이 실제로 바뀔 때만 올린다.
        const payload = JSON.stringify({
            action: action,
            data: data || null,
            callback: callback || null,
            version: '1.0'
        });

        let r;
        try {
            r = window.App.appBroker(payload);
        } catch (e) {
            return { ok: 0, data: null, err: { code: 'INTERNAL', msg: String(e) } };
        }

        // 구앱 — 1/0 (int 또는 "1"/"0"). 느슨한 비교 유지.
        if (typeof r !== 'string' || r.charAt(0) !== '{') {
            return { ok: (r == 1) ? 1 : 0, data: null, err: null };
        }

        // 신앱 — {ok, data, err}
        try {
            const res = JSON.parse(r);
            if (!res || res.ok != 1) return { ok: 0, data: null, err: (res && res.err) || null };
            return { ok: 1, data: (res.data !== undefined ? res.data : null), err: null };
        } catch (e) {
            return { ok: 0, data: null, err: { code: 'BAD_PAYLOAD', msg: String(r).slice(0, 200) } };
        }
    }

    /**
     * 안드로이드 앱함수를 호출하는 브로커함수 (수락 여부만 필요한 호출부용)
     * 즉시 반환값(data)이 필요하면 callBroker() 를 쓸 것.
     *
     * 앱 밖(window.App 없음) 이면 "안드로이드앱에서 실행해주세요" 안내 —
     * 단 **PC 브라우저에서는 띄우지 않는다**. 개발·확인 목적으로 PC 에서 여는 경우가 많고,
     * 페이지 진입만으로 자동 호출되는 액션(getDeviceContext 등)까지 alert 를 띄우면 방해만 된다.
     * 폰 브라우저(앱 아님)는 실제로 앱에서 열어야 하는 상황이므로 그대로 안내한다.
     *
     * @param action
     * @param data
     * @param callback
     * @returns {boolean}
     */
    runAppBroker(action, data=null, callback=null){
        if (!this.isApp()) {
            // 안내는 앱이 **아예 없을 때**만 — 대리진입은 window.App 이 살아 있으므로 조용히 지나간다.
            // (앱 웹뷰에서 alert 를 띄우면 닫기 전까지 모든 브릿지가 멈춰 관리자 복귀조차 못 한다)
            if (!window.App) {
                if ($j.Common.Util.isMobile()) alert('장비불러 앱에서 실행해주세요.');
                else console.warn(`[Android] 앱 밖(PC)에서 호출됨 — 무시: ${action}`);
            }
            // callBroker 를 타지 않고 끝나므로 디버깅 로그는 여기서 직접 찍는다
            if (!AppBridge.DBG_MUTE.includes(action)) this._dbgSend(action, data, { ok: 0, data: null, err: null });
            return false;
        }
        if (typeof window.App.appBroker !== 'function') return false;

        return this.callBroker(action, data, callback).ok == 1;
    }

    /**
     * 이미지 1개 공유 — 앱 공유시트.
     *   url      : 앱이 그 주소에서 내려받아 공유한다 (jpg·jpeg·png·pdf). 상대경로는 절대주소로 바꿔 보낸다
     *   data URL : 'data:image/(jpeg|png|webp);base64,…' 를 그대로 공유한다 — 서버에 올리지 않는다.
     *              shareDataSupported() 인 앱만 안다. 본문 2.8MB(원본 약 2MB) 초과·다른 MIME 은 앱이 거절(ok:0)
     * @param {string} src       이미지 주소 또는 data URL
     * @param {string} [filename] data URL 일 때만 — 생략하면 앱이 share_<시각>.<확장자> 로 짓는다
     * @returns {boolean} 앱이 수락했는가 (공유시트·파일쓰기는 비동기 — 그 뒤 실패는 앱이 토스트로 알린다)
     */
    shareFile(src, filename) {
        const isData = /^data:/.test(String(src));
        return this.runAppBroker('shareFile',
            isData ? { data: src, filename: filename } : { url: new URL(src, location.href).href });
    }

    /**
     * 앱이 data URL 공유(shareFile 의 data)를 아는가 (1회 캐시) — pickerSupported() 와 같은 꼴.
     * getCapabilities.shareData 로 감지. 구앱은 data 를 몰라 공유시트가 안 뜨는데도 ok:1 이 오므로 반드시 먼저 본다 —
     * false 면 이미지를 서버에 잠깐 올려 그 주소로 shareFile 한다(구앱 경로).
     * @returns {boolean}
     */
    shareDataSupported() {
        if (this._shareDataSupported == null) {
            const res = this.callBroker('getCapabilities');
            this._shareDataSupported = !!(res.ok == 1 && res.data && res.data.shareData == 1);
        }
        return this._shareDataSupported;
    }

    /**
     * 장비불러 계정 onetime 인증 시작 (악덕알리미 "빠른 시작").
     * 앱이 장비불러앱을 통해 onetime 토큰을 발급받아 badcall://login?token= 딥링크로 회신 →
     * WebView 가 /b/login/onetime?token= 으로 진입(세션 로그인, OneTimeLoginController@consume).
     * @returns {boolean}
     */
    jbLogin(app = 'BD') {
        return this.runAppBroker('jbLogin', { app });
    }

    /**
     * 로그아웃 통지 — 웹 세션 로그아웃 직전 앱에 알린다(푸시토큰/세션 정리 등).
     * 회신 콜백 없음. 앱 외 환경(window.App 없음)에서는 조용히 무시한다.
     * @returns {boolean}
     */
    logout() {
        if (!(window.App && typeof App.appBroker === 'function')) return false;
        return this.runAppBroker('logout');
    }

    /**
     * 단회용 토큰 핸드오프 — 장비불러 WebView(/m/auth/onetime)에서 발급된 토큰을 네이티브에 전달.
     * 네이티브는 app(요청 서브앱) 으로 콜백 대상을 결정해 회신(Activity Result 등) → 해당 앱 consume.
     * @param {string} token
     * @param {string} app  요청 서브앱 코드(BD/MC ...)
     * @returns {boolean}
     */
    onetimeToken(token, app = 'BD') {
        return this.runAppBroker('onetimeToken', { token, app });
    }

    /**
     * 이미지 업로드 시작
     *
     * 대리진입(관리자가 남의 계정) 중에도 보낸다 — callBroker 의 ignoreProxy. 업로드는 회원 기기값을
     * CS 폰에 심지 않는다: /api/upload 는 파일만 저장하고 FCM 토큰·현위치 행을 건드리지 않으며,
     * 현장사진 워터마크의 gps 는 CS 폰의 현위치가 아니라 **사진 EXIF** 값이다. 올린 사람은
     * manage_num 으로 남는다. 막으면 CS 가 회원 대신 사진·서류를 올리는 업무가 통째로 죽는다.
     * (.ai/proxy-login.md §4-4)
     *
     * @param tag
     * @param limit
     * @param from
     * @param id
     * @param {object} [opts]
     * @param {boolean} [opts.original] true면 앱이 리사이즈·재압축을 건너뛰고 촬영/선택 원본
     *   해상도 그대로 올린다 — OCR처럼 화질이 결과에 직결되는 태그만 켠다(일반 사진은 그대로
     *   압축). 구앱은 이 필드를 모르고 무시하므로 originalUploadSupported() 로 지원 여부를
     *   먼저 확인하는 게 안전하다(무조건 켜도 에러는 안 나지만 구앱에서는 조용히 압축된다).
     * @param {string} [opts.guide] 'card'면 from:'C' 촬영을 시스템 카메라 대신 앱 안 카드
     *   스캔 화면(가이드 프레임)으로 띄운다 — 카드 영역만 잘라 원본 해상도로 올려준다.
     *   from:'G'(갤러리)에는 영향 없다. 구앱은 이 필드도 몰라서 무시(시스템 카메라로 자연
     *   폴백) — 별도 지원여부 게이트 없이 항상 켜도 된다.
     * @param {boolean} [opts.pdf] true면 앱이 2장 이상 고른 사진을 PDF 한 파일로 묶어 한 번만
     *   올린다 — 콜백은 파일 하나(type 'F', name 은 앱이 지은 파일명). 1장(촬영 포함)이면 사진 그대로
     *   (type 'I') 올라오니 수신부는 type 으로 가른다. 여러 쪽짜리 서류를 파일 하나로 받아야 하는 곳만 켠다. 구앱은 이 키를 몰라 낱장 사진으로 올리므로
     *   pdfUploadSupported() 로 먼저 가른다. PC 경로(file input)엔 적용되지 않는다(이미지 그대로).
     * @param {number} [opts.pdfMaxBytes] 묶은 PDF 의 크기 한도(바이트). 앱이 넘으면 화질을 낮춰 다시 묶고, 끝까지
     *   넘으면 올리지 않고 회원에게 장 수를 줄이라고 안내한 뒤 {result:false, message} 로 알린다. 한도는 업무 규칙
     *   (이노페이 첨부 5.5MB 등)이라 웹이 넘긴다 — 앱은 모른다. 0/생략 = 한도 없음.
     *
     * `original`·`guide` 는 **`need` 안(정본, §12-2)과 최상단 평평한 칸 양쪽에** 싣는다 — 3.20 부터는 `need` 를,
     * 그 전 앱(3.1943, 기존 카드결제가 도는 버전)은 최상단 `original`·`guide` 를 읽는다.
     * 평평한 칸은 3.1943 이 시장에서 빠지면 걷어낸다(.ai/app-protocol.md §12-2). `pdf` 처럼 새 요구는 `need` 에만 싣는다.
     */
    startUploadImage(tag, limit = 1, from = null, id = null, opts = {}) {
        const data = {type: 'I', tag, limit};
        if (from) data.from = from;
        if (id) data.id = id;
        const need = {};
        if (opts.original) need.original = 1;
        if (opts.guide)    need.guide    = opts.guide;
        if (Object.keys(need).length) Object.assign(data, need, { need });
        if (opts.pdf) {   // need 에만 — 평평한 칸 없음
            data.need = Object.assign(data.need || {}, { pdf: 1 });
            if (opts.pdfMaxBytes > 0) data.need.pdfMaxBytes = Math.floor(opts.pdfMaxBytes);
        }
        return this.callBroker('upload', data, '$j.F.App.callbackUploadImage', true).ok == 1;
    }

    /**
     * 앱이 사진을 PDF 한 파일로 묶어 올리기(upload need.pdf)를 아는가 (1회 캐시) — getCapabilities.uploadPdf.
     * 구앱은 need.pdf 를 몰라 낱장 사진(type 'I' 여러 건)으로 올리므로, 파일 하나를 기대하는 화면은 이걸 먼저 본다 —
     * 아니면 PDF 선택(from:'F')이나 낱장 업로드로 간다.
     * 대리진입 중에도 본다(ignoreProxy) — fileUploadSupported() 와 같은 이유.
     * @returns {boolean}
     */
    pdfUploadSupported() {
        if (this._pdfUploadSupported == null) {
            const res = this.callBroker('getCapabilities', null, null, true);
            this._pdfUploadSupported = !!(res.ok == 1 && res.data && res.data.uploadPdf == 1);
        }
        return this._pdfUploadSupported;
    }

    /**
     * 앱이 업로드 원본유지(original 옵션)를 지원하는가 (1회 캐시) — pickerSupported() 와 같은 꼴.
     * 부작용 없는 getCapabilities 브로커로 감지한다. 구앱·웹은 {ok:0} → false.
     * 대리진입 중에도 본다(ignoreProxy) — fileUploadSupported() 와 같은 이유.
     * @returns {boolean}
     */
    originalUploadSupported() {
        if (this._originalUploadSupported == null) {
            const res = this.callBroker('getCapabilities', null, null, true);
            this._originalUploadSupported = !!(res.ok == 1 && res.data && res.data.uploadOriginal == 1);
        }
        return this._originalUploadSupported;
    }

    /**
     * 앱이 PDF 파일 업로드(from:'F')를 아는가 (1회 캐시) — getCapabilities.uploadFile.
     * 구앱은 from:'F' 를 몰라 엉뚱한 선택창을 띄우므로, PDF 를 앱에 맡기기 전에 이걸 본다.
     * PDF 는 앱이 원본 그대로 올리고 콜백 type 은 'F'(이미지 처리 없음).
     * 대리진입 중에도 본다(ignoreProxy) — 업로드는 대리 중에도 앱으로 가므로(startUploadImage) 판정도 같이 가야 한다.
     * getCapabilities 는 앱이 지원 목록만 돌려주는 조회라 회원 행에 남는 것이 없다(.ai/proxy-login.md §4-4).
     * @returns {boolean}
     */
    fileUploadSupported() {
        if (this._fileUploadSupported == null) {
            const res = this.callBroker('getCapabilities', null, null, true);
            this._fileUploadSupported = !!(res.ok == 1 && res.data && res.data.uploadFile == 1);
        }
        return this._fileUploadSupported;
    }

    /**
     * 디바이스 사진 목록 요청 — **목록만 준다. 그리는 것은 웹이다**(`$j.Ui.DevicePicker`).
     *
     * 앱은 즉시 `{ok:1,data:null}` 로 수락만 하고, 실제 목록은 백그라운드 조회 후
     * callbackPhotos 로 회신한다 (MediaStore 쿼리 + 썸네일 인코딩이라 동기 반환 불가).
     *
     * ⚠ 레거시 폴백 없음 — 구앱은 `ok:0`. 호출부가 네이티브 피커로 분기해야 한다.
     *   **이 `ok:0` 이 곧 지원여부 판정이다** — 액션명을 모르면 앱이 거부하므로, 목록 요청에
     *   부작용이 생기지 않는다. 그래서 별도 capability 플래그가 필요 없다 (.ai/app-protocol.md §10-0).
     *
     * @param {object}  [opts]
     * @param {number}  [opts.limit]     한 페이지 장수
     * @param {string}  [opts.cursor]    앱이 준 불투명 커서 (첫 페이지는 생략)
     * @param {boolean} [opts.reselect]  true면 OS 권한 재선택창을 띄우고 **첫 페이지를 다시** 보낸다
     *   (Android 14 부분허용 `permission:'partial'` 에서 "사진 더 선택"). 모르는 앱은 이 필드를
     *   무시하고 그냥 첫 페이지를 주므로 — 창이 안 뜰 뿐 결과는 옳다.
     * @param {string}  [opts.since]  촬영시각 하한 `'YYYY-MM-DD HH:mm:ss'` (기기 로컬, 포함)
     * @param {string}  [opts.until]  촬영시각 상한. 구간을 걸면 **앱이 적용한 구간을 응답 `did` 로
     *   되돌려준다** — `did` 가 없으면 필터가 안 걸린 전체 목록이다(웹이 확인해야 한다).
     * @returns {boolean} true=앱이 수락(콜백 기다림) / false=구앱·웹 환경
     */
    listPhotos({ limit = 30, cursor = null, reselect = false, since = null, until = null } = {}) {
        const data = { limit, cursor };
        if (reselect) data.reselect = 1;
        if (since)    data.since    = since;
        if (until)    data.until    = until;
        return this.callBroker('listPhotos', data, '$j.F.App.callbackPhotos').ok == 1;
    }

    /**
     * 격자 사진 한 장을 크게 — 확대 보기용. 앱이 그 사진을 긴 변 1600px JPEG 로 만들어 callbackPhotoPreview 로 회신한다
     * (`{token, url:'data:image/jpeg;base64,…', w, h}` / 실패 `{token, error}`). 썸네일(작은 그림)은 키우면 글자를 못 읽어서
     * 큰 그림은 앱이 만든다(2026-10-27). 지원 판별은 photoPreviewSupported() — 구앱은 `ok:0`.
     * @param {string} token listPhotos 가 준 불투명 식별자
     * @returns {boolean} true=앱이 수락(콜백 기다림)
     */
    previewPhoto(token) {
        if (!token) return false;
        return this.callBroker('previewPhoto', { token: String(token) }, '$j.F.App.callbackPhotoPreview').ok == 1;
    }

    /**
     * 앱이 격자 사진 확대(previewPhoto)를 아는가 (1회 캐시) — getCapabilities.photoPreview.
     * 모르는 앱에서는 확대 버튼을 그리지 않는다(누르면 아무것도 안 되는 버튼을 두지 않는다).
     * @returns {boolean}
     */
    photoPreviewSupported() {
        if (this._photoPreviewSupported == null) {
            const res = this.callBroker('getCapabilities');
            this._photoPreviewSupported = !!(res.ok == 1 && res.data && res.data.photoPreview == 1);
        }
        return this._photoPreviewSupported;
    }

    /**
     * 앱이 디바이스 사진 그리드 피커를 지원하는가 (격자 vs 네이티브 게이트 판별용, 1회 캐시).
     *
     * 부작용 없는 `getCapabilities` 브로커로 감지한다 — 실제 피커 액션(listPhotos 등)으로 떠보면
     * 구앱에서 네이티브 피커가 같이 열리는 부작용이 있어, 전용 감지 액션을 쓴다.
     * appBroker 없는 앱(구 mc)·모르는 앱(jb)·웹은 callBroker 가 {ok:0} → false.
     * data.picker 까지 확인해 구앱 스칼라 1 오탐도 막는다.
     * @returns {boolean}
     */
    pickerSupported() {
        if (this._pickerSupported == null) {
            const res = this.callBroker('getCapabilities');
            this._pickerSupported = !!(res.ok == 1 && res.data && res.data.picker == 1);
        }
        return this._pickerSupported;
    }

    /**
     * 그리드 피커에서 고른 사진들을 앱이 일괄 업로드 (token = listPhotos 가 준 불투명 식별자)
     *
     * 완료 통지는 기존 업로드 경로와 동일하게 callbackUploadImage → EventBus 'upload:image'.
     *
     * ⚠ 레거시 폴백 없음 — 구앱은 `ok:0`. (`upload` 액션에 tokens 를 얹으면 구앱이 이를 무시하고
     *   OS 피커를 열어버리므로 별도 액션으로 분리했다.)
     *
     * @param {string} tag  업로드 tag ('diary1' 등) — 사진 분류 tag(작업전/중/후)와 다르다
     * @param {number|string|null} id  tag 관련 키 (현장사진이면 오더번호)
     * @param {Array<string>} tokens  선택한 사진 token 목록
     * @param {object} [need]  `upload` 와 같은 요구 봉투 `{text, mask, guide, original}` (§12-2). 격자로 고른 사진이라고
     *   가림·원본유지가 달라질 이유가 없다. 없으면 싣지 않는다(기존과 같은 페이로드).
     * @returns {boolean} true=앱이 수락 / false=구앱·웹 환경
     */
    uploadSelectedPhotos(tag, id, tokens, need = null) {
        const data = { type: 'I', tag, tokens: tokens || [] };
        if (id) data.id = id;
        if (need && typeof need === 'object') data.need = need;
        return this.callBroker('uploadSelectedPhotos', data, '$j.F.App.callbackUploadImage').ok == 1;
    }

    /**
     * 음성을 듣는다 STT
     * @param howinput
     * @param act
     * @param ttsword
     * @param useSpeech_setValue
     */
    listenStartVoice(howinput, act, ttsword, useSpeech_setValue) {
        return this.runAppBroker("listenStartVoice", {howinput, act, ttsword, useSpeech_setValue});
    }

    /**
     * 음성 듣기를 중단한다.
     */
    listenEndVoice() {
        return this.runAppBroker("listenEndVoice");
    }

    /**
     * 알림채널 개수 초과 이슈로 알림음 미동작시 앱 삭제 안내를 위해 팝업에 들어갈 문구
     * @param {boolean} shouldShow - 팝업 표시 여부를 결정
     * @returns {boolean}
     */
    setPopUpConfig(shouldShow) {
        if (window.App && typeof App.appBroker !== 'undefined') {
            const data = {
                showPopup: shouldShow,
                popupConfig: {
                    dialogTitle: '알림 설정 오류',
                    dialogMessage: '아래 링크를 통해 앱을 제거 후 재설치 해주세요\n\n - 앱을 제거하셔도 계정/정보는 안전하게 보호됩니다\n##- 닫기 시 알림이 정상적으로 작동하지 않을 수 있습니다##',
                    allowBackButton: false,
                    allowOnOutsideTouch: false,
                    positiveButtonName: "제거",
                }
            };
            this.runAppBroker('displayPopup', data, '$j.F.App.callbackChannelCount');
        }
        return true;
    }

    /**
     * 핸드폰번호를 가져온다. → callbackPhoneNumber 로 회신
     */
    getPhoneNumber() {
        return this.runAppBroker("getPhoneNumber", null, '$j.F.App.callbackPhoneNumber');
    }

    /**
     * 디바이스 컨텍스트를 가져온다 (GPS · 주소 · FCM · 앱버전 · 모델 · 회원번호 · GPS정확도).
     * → callbackDeviceContext 로 회신 → EventBus 'app:device:context' → 사이트 모듈이 반영
     *   (장비불러는 $j.F.JbApp._applyDeviceContext 가 서버 저장·폼 반영)
     */
    getDeviceContext() {
        // ⚠ 여기에 대리진입 가드를 넣지 않는다. 장비불러의 응답 반영($j.F.JbApp._applyDeviceContext)은 서버 저장 외에
        //   ① 열려 있는 지도를 현위치로 이동(moveMap — CS 대신발주에 쓴다) ② 로그인폼에 토큰 심기
        //   를 함께 한다. 반환값을 보는 곳도 있다($j.F.KakaoMap.map_adr_to_input → false 면 국토중심으로 튄다).
        //   회원 행 저장은 서버 MemberProfileService::updateDevice 가 확실히 막는다.
        return this.runAppBroker("getDeviceContext", null, '$j.F.App.callbackDeviceContext');
    }

    /**
     * 현위치 수동 새로고침 — GPS 설정화면(/m/setting/gps)의 '새로고침' 버튼.
     * 저장 throttle 쿠키를 지워 다음 응답이 반드시 서버에 반영되게 한 뒤 디바이스 컨텍스트를
     * 다시 받고, 화면에 새 위치를 보이려고 잠시 뒤 페이지를 리로드한다.
     * (구 public/js/mobile.js 의 전역 reload_mypos)
     *
     * @param {number} mbNo 회원번호 (throttle 쿠키 키에 쓰인다)
     */
    refreshMyPosition(mbNo) {
        Store.cookie.remove(mbNo + '_saveInfo_last_Timebuller');
        this.getDeviceContext();
        setInterval(function () {
            document.location.reload();
        }, 1000);
    }

    /**
     * GPS 권한 허용 여부를 **즉시** 조회한다 (앱 메모리 조회 — 콜백 없음).
     *
     * ⚠️ 레거시 폴백 없음. 구앱은 이 액션을 모르므로 `0`(거부) 을 반환하고,
     *    그 경우 "권한 없음" 이 아니라 **"알 수 없음"** 이다 — 반드시 null 로 구분할 것.
     *    (구앱에게 권한 안내 UI 를 띄우면 오안내가 된다.)
     *
     * 앱 계약: `{"ok":1,"data":{"granted":1|0},"err":null}`
     *
     * @returns {number|null} 1=허용, 0=거부, null=알 수 없음(구앱·웹 환경·미지원)
     */
    getLocationPermission() {
        const res = this.callBroker('getLocationPermission');
        if (res.ok != 1 || res.data === null) return null;
        // 계약은 {granted} 객체. 앱이 스칼라(1/0)로 주는 경우도 관용 처리.
        const granted = (typeof res.data === 'object') ? res.data.granted : res.data;
        return (granted == 1) ? 1 : 0;
    }

    /**
     * GPS 권한 요청 화면(시스템 다이얼로그)을 띄운다.
     *
     * 사용자 상호작용이 필요하므로 즉시값이 없다 — 수락 여부만 즉시 반환하고,
     * 사용자의 선택 결과는 callbackLocationPermission 으로 회신된다.
     * ⚠️ 레거시 폴백 없음. 구앱이면 false 를 반환하므로 호출부가 자체 안내를 해야 한다.
     *
     * @returns {boolean} true=앱이 권한요청을 띄움, false=구앱·웹 환경·거부
     */
    requestLocationPermission() {
        return this.callBroker(
            'requestLocationPermission', null, '$j.F.App.callbackLocationPermission'
        ).ok == 1;
    }

    /**
     * 분석용 로그를 서버로 전송한다.
     * @param x 구분값 app(장비불러),devils(악덕알리미),...
     */
    getDebugLog(x) {
        return this.runAppBroker("getDebugLog", {x});
    }

    /**
     * 설정 동기화 (통합 진입점) — 'syncSettings' 액션으로 4개 도메인을 한 번에 처리.
     *
     * 부분 업데이트 가능 — 변경된 도메인만 포함하면 됨.
     *
     * @param {Object} settings
     * @param {Object} [settings.push]   - {noticeOn, matchingOn, vibrate, darkMode} 푸시 알림 on/off + 진동 + 다크모드
     *                                     · (악덕알리미 BD 확장) {alertMode:'sound'|'sound_vibrate'|'vibrate'|'silent'}
     *                                       — BD 전용 단일 알림모드. JB 네이티브엔 없는 필드, BD 네이티브 broker 단독 책임.
     *                                       (20260527_BADCALL_APP_BROKER_ACTIONS.md §6)
     * @param {Object} [settings.tts]    - {enabled, type, speed, pitch} TTS 설정 (JB 전용)
     * @param {Object} [settings.sound]  - {matchingFn, noticeFn} 알림음 파일명 (JB 전용)
     * @param {Object} [settings.member] - {mbno, isAdmin} 회원 컨텍스트 (BD 는 {mbno, phone, phoneCheck} — 번호 대조, app-protocol §6)
     * @param {string} [settings.theme]  - 'light'|'dark' 화면 테마 (악덕알리미 BD 전용 — 네이티브 상태바/스플래시 테마 동기화). JB 는 push.darkMode(0/1) 사용.
     * @returns {boolean}
     *
     * @example
     *   $j.F.App.syncSettings({
     *       push: { noticeOn: 'YES', matchingOn: 'YES', vibrate: 1 },
     *       tts:  { enabled: 'YES', type: 1, speed: 1.0, pitch: 1.2 },
     *   });
     */
    syncSettings(settings = {}) {
        // broker 페이로드 정규화 — tts.enabled 는 contract 상 'YES'/'NO'.
        // 호출부가 정수(0/1) 로 넘기는 경우가 많아(Android 측에서 매칭 실패 → TTS off 무시 회귀)
        // 여기서 한 번 변환한다. (TTS OFF 동기화 버그, 2026-05-13)
        let brokerSettings = settings;
        if (settings.tts && settings.tts.enabled !== undefined
            && settings.tts.enabled !== 'YES' && settings.tts.enabled !== 'NO') {
            brokerSettings = {
                ...settings,
                tts: {
                    ...settings.tts,
                    enabled: (parseInt(settings.tts.enabled, 10) === 1) ? 'YES' : 'NO',
                },
            };
        }
        return this.runAppBroker("syncSettings", brokerSettings);
    }

    /**
     * 음성파일을 플레이한다 ( 꼬끼오, MBC로고송 등등 )
     * @param fn
     */
    playSound(fn) {
        if (fn === "def") return true;
        return this.runAppBroker("playSound", {fn});
    }

    /**
     * 입력된 글자를 TTS 로 읽어준다.
     * @param text
     */
    readTts(text) {
        return this.runAppBroker("readTts", {text});
    }

    /**
     * 웹뷰의 상태를 앱에게 알린다. 웹뷰의 doze 모드여부를 앱에게 알리기위함
     */
    webviewHealthCheck() {
        return this.runAppBroker("hc");
    }

    /**
     * 소셜 연동 — 결과는 callbackSocialResult → EventBus 'app:social:result' 로 회신.
     *
     * @param snsType naver(네이버), kakao(카카오)
     * @returns {boolean} true(앱에서 인증처리), false(웹에서 인증처리)
     */
    runSocial(snsType) {
        if (!window.App) return false;
        $j.Common.Util.loadingOverlay(true);
        const t = snsType === 'naver' ? 'N' : 'K';
        const result = this.runAppBroker("social", {type: t});
        $j.Common.Util.loadingOverlay(false);
        return result;
    }

    /**
     * 카카오내비 앱 실행 (경도, 위도 순으로 넘겨준다)
     * @param lng 경도
     * @param lat 위도
     * @param addr 주소
     */
    runNavi(lng, lat, addr) {
        if (!window.App) return false;

        // 앱 버전에 따라 SDK 버전 결정
        let sdk_ver = 1; // 기본값은 1 (3.185 버전용)
        if (MEMBER['mobie_ver'] && parseFloat(MEMBER['mobie_ver']) >= 3.186) {
            sdk_ver = 2; // 3.186 이상 버전용
        }
        return this.runAppBroker("kakao_navi", {lng: String(lng), lat: String(lat), addr: addr, sdk_ver: sdk_ver});
    }

    /**
     * 로드뷰를 오픈한다.
     * @param gps
     */
    openRoadView(gps) {
        const url = `https://map.kakao.com/link/roadview/${gps}`;
        if (window.App && typeof App.appBroker !== 'undefined') {
            return this.runAppBroker("openRoadView", {gps: gps});
        } else {
            window.open(url);
        }
    }

    /**
     * 이미지를 다운로드한다. 앱이 받아주지 않으면(웹 환경 등) 브라우저 다운로드로 넘긴다.
     * ※ 앱 레거시 폴백이 아니라 웹 환경 폴백이다 — callBroker 로 보내 alert 를 띄우지 않는다.
     * @param url 다운로드할 이미지 URL
     */
    downloadImage(url) {
        url = new URL(url, location.href).href;
        if (this.callBroker("imageDownload", {urlList: [url]}).ok == 1) return true;
        if (window.App) { document.location.href = url; return true; } // 웹뷰는 blob 저장을 못 한다 — 현행 유지

        // 브라우저(PC 관리자 등): 이미지 서버가 다른 도메인이라 주소로 넘기면 저장이 아니라 그 자리에
        // 이미지가 열린다. blob 으로 받아야 '저장'이 된다. fetch 가 막히면 새 창으로 연다.
        const name = decodeURIComponent(new URL(url).pathname.split('/').pop() || 'image');
        fetch(url)
            .then((res) => { if (!res.ok) throw new Error(res.status); return res.blob(); })
            .then((blob) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
            })
            .catch(() => window.open(url, '_blank'));
        return true;
    }

    /**
     * 연락처 팝업을 띄운다.
     */
    openAddressBook() {
        return this.runAppBroker('addressBook', null, '$j.F.App.callbackAddressBook');
    }

    /**
     * 현재 페이지 코드를 앱에 알린다.
     *
     * Android 에서 "지금 알림페이지인지 / 수주페이지인지" 를 판단할 때
     * `webView.getUrl().contains("/m/alim")` 식 하드코딩 매칭을 쓰지 않도록 함.
     * 웹이 SoT (Source of Truth) — 진입 시점에 페이지 코드를 통보 → Android 가
     * 메모리에 저장해 두고 어디서든 동기 조회. 웹 측 URL 이 바뀌어도 페이지코드표
     * (서버 AppRegistry pages) 한 곳만 갱신하면 됨 (Android 코드 무관).
     *
     * code 가 null(매핑 없는 페이지)인 경우도 발화 → Android 가 "기타 페이지"
     * 로 인식해서 마지막 매핑 코드에 갇히지 않게 함.
     *
     * @param {string} [code] 페이지 코드. 미지정 시 페이지코드표 매칭으로 자동 결정
     * @returns {boolean}
     */
    setCurrentPage(code) {
        if (!window.App) return false;
        const finalCode = code !== undefined ? code : this._getPageCode();
        return this.runAppBroker('setCurrentPage', { code: finalCode });
    }

    /**
     * 장비불러 플레이스토어를 오픈한다.
     */
    openPlaystore() {
        return this.runAppBroker('openPlaystore');
    }

    /**
     * 등록된 스토어 주소를 앱이 열어 준다 — 웹뷰가 스토어 전용 주소(onestore:// 등)를 못 여는 앱에서 쓴다.
     * 액션을 모르는 구앱은 false — 호출부가 대체 경로로 간다.
     *
     * @param {string} url g5_apps.store_url
     * @returns {boolean} 앱이 스토어를 열었으면 true
     */
    openStore(url) {
        return this.callBroker('openStore', { url }).ok == 1;
    }

    /**
     * 앱이 접속할 도메인(호스트)을 교체한다 — 개발/테스트 전용 (/m/dev/bridge).
     *
     * 네이티브가 저장한 base host 를 갈아끼우고 해당 호스트로 웹뷰를 재진입시킨다.
     * 회신 콜백 없음 — 성공하면 앱이 곧바로 새 호스트로 이동하므로 현재 페이지는 사라진다.
     * ⚠️ 레거시 폴백 없음. 구앱은 액션을 모르므로 false 를 반환한다.
     *
     * @param {string} host 스킴 포함 오리진 ('https://jbbuller.kr', 끝 슬래시 없음)
     * @param {string} tag  호스트 구분 라벨 ('운영'·'스테이지'·'QA'·'개발1' …)
     * @returns {boolean} true=앱이 수락, false=구앱·웹 환경·거부
     */
    changeHost(host, tag = '') {
        return this.runAppBroker('changeHost', { host, tag });
    }

    /**
     * 웹뷰 history 초기화 요청 — 현재 페이지를 뒤로가기 루트로 만든다(그 이전 스택 제거).
     * 로그인 직후 첫 화면에서 호출해 로그인/리다이렉트 페이지를 뒤로가기 대상에서 지운다
     * (첫 화면에서 백버튼 → 로그인으로 안 돌아가고 앱 종료 흐름).
     * 앱 계약: 네이티브가 webView.clearHistory() 호출. 회신 없음, fire-and-forget.
     * @returns {boolean} 앱이 수락하면 true (구앱·웹은 false)
     */
    clearHistory() {
        // 대리진입 중에도 보낸다(ignoreProxy) — 히스토리 정리는 회원 데이터를 CS 폰에 심는 일이
        // 아니고, 스택이 남으면 안드로이드 뒤로가기로 CS 본인 화면이 회원 세션 위에 뜬다.
        return this.callBroker('clearHistory', null, null, true).ok == 1;
    }

    /**
     * 로그인 직후 첫 화면이면 웹뷰 history 를 초기화한다 (앱 한정, 로그인당 1회).
     *
     * loginKey 는 로그인마다 새로 발급돼 MEMBER.loginKey 로 실려온다(LoginService::updateMemberSession).
     * 직전에 본 loginKey 와 다르면 = 새 로그인의 첫 화면 → clearHistory 로 로그인·리다이렉트 페이지를
     * 뒤로가기 스택에서 지운다. 부트스트랩에서 매 페이지 호출하지만 loginKey 비교로 로그인당 1회만 발화.
     * (세션 판정 방식은 LocationGate 의 loginKey 재로그인 무효화와 동일.)
     */
    clearHistoryOnFreshLogin() {
        // isApp() 이 아니라 window.App 을 본다 — 대리진입도 mobileLogin 을 거쳐 새 loginKey 를
        // 받으므로 여기서 히스토리를 비워야 한다. isApp() 은 대리 중 false 라(회원 데이터가 CS 폰에
        // 심기는 것을 막는 판정) 그걸 쓰면 스택이 남고, 안드로이드 뒤로가기로 CS 본인 계정 화면이
        // 회원 세션 위에 뜬다. ProxyBar._back 이 location.replace 를 쓰는 것과 같은 이유다.
        if (!window.App) return;   // 웹뷰(앱)에서만 의미 있음
        if (typeof MEMBER === 'undefined' || !MEMBER || !MEMBER.loginKey) return;

        const KEY = 'app:auth:lastLoginKey';
        if ($j.Common.Store.session.get(KEY) === MEMBER.loginKey) return;  // 같은 로그인 — 이미 처리
        $j.Common.Store.session.set(KEY, MEMBER.loginKey);
        this.clearHistory();
    }

    /* ── 악덕알리미(co.kr.badcall) 연결 — jb 햄버거 '악덕업자 메뉴' → 연결 페이지(/m/relation/devil/app) ──
     * 앱을 여는 건 네이티브(OpenAppHandler, 브로커 `openApp`)가 한다: ① 딥링크 스킴 보유(1.70↑)면
     * {G5_DEEPLINK_BD}?code=001&app=BD(co.kr.badcall://app?…) 로 악덕홈(/b) ② 스킴 없는 구버전(1.68)이라도 깔려 있으면 런처 인텐트(홈)
     * ③ 미설치면 fallback(https) 을 외부 브라우저로. 웹뷰 안 숨은 iframe 으로 스킴을 쏘던 옛 방식은 서브프레임 스킴이 앱 실행으로
     * 이어지지 않는 경우가 있어(jbbuller_doc badcall/202606/20260626_DEEPLINK_JB_TO_DV_OPEN_BUG) 걷어냈다.
     * 스토어 이름·스킴은 앱 화면에 두지 않는다(구글 정책) — 안내 웹(/devil-install)이 브라우저에서 스토어 링크를 맡는다.
     * 패키지·스킴은 코드에 박지 않고 G5.APPS(AppRegistry)·G5.DEEPLINK_BD 를 읽는다.
     */

    /** 이 앱이 openApp 을 아는가(새 jb). getCapabilities 로 감지, 1회 캐시 — pickerSupported 와 같은 꼴. 구앱·웹은 false */
    devilAppSupported() {
        if (this._devilAppSupported == null) {
            const res = this.callBroker('getCapabilities');
            this._devilAppSupported = !!(res.ok == 1 && res.data && res.data.openApp == 1);
        }
        return this._devilAppSupported;
    }

    /**
     * 악덕알리미가 이 폰에 깔려 있는가 — 실행하지 않고 판정만(openApp check:1). 연결 페이지가 칩·버튼을 그리는 데 쓴다.
     * @returns {boolean|null} null=알 수 없음(구앱·브라우저)
     */
    devilAppInstalled() {
        if (!window.App || typeof window.App.appBroker !== 'function') return null;
        const res = this.callBroker('openApp', { pkg: this._bdPackage(), url: this._bdHomeUrl(), check: 1 });
        if (res.ok != 1 || !res.data || res.data.installed === undefined) return null;
        return res.data.installed == 1;
    }

    /**
     * 악덕알리미 열기. 깔려 있으면 앱이 열리고, 없으면 fallback(https) 을 외부 브라우저로 연다(비우면 아무것도 안 함).
     * @param {{fallback?: string}} [opts] 미설치 시 브라우저로 열 주소 — 연결 페이지는 location.origin + '/devil-install'
     * @returns {'opened'|'browser'|'none'|false} false=구앱(액션 모름)·브라우저 환경
     */
    openDevilApp({ fallback = '' } = {}){
        if (!window.App || typeof window.App.appBroker !== 'function') return false;
        const res = this.callBroker('openApp', { pkg: this._bdPackage(), url: this._bdHomeUrl(), fallback });
        if (res.ok != 1) return false;                              // 구앱(UNSUPPORTED_ACTION)·오류
        if (!res.data || res.data.opened === undefined) return 'opened';   // 즉시값을 안 주던 중간 빌드 — 앱이 제 몫을 한 것으로
        if (res.data.opened == 1) return 'opened';
        return res.data.step === 'browser' ? 'browser' : 'none';
    }

    _bdPackage() { return (this._apps().apps.BD || {}).package || 'co.kr.badcall'; }
    _bdHomeUrl() {
        const link = (typeof G5 !== 'undefined' && G5['DEEPLINK_BD']) || 'co.kr.badcall://app';
        return `${link}?code=001&app=BD`;   // 악덕홈(/b) 페이지코드 001
    }

    /* =============================================================================
     *  2. App → Web  ─  콜백 (요청-응답형)
     * =============================================================================
     *  - callbackBroker : 단일 진입점 (점경로/멤버 디스패치, eval 없음)
     *  - 이하 callback* : §1 의 broker 송신에 대한 응답.
     *      웹이 요청 → Android 처리 → callbackBroker 경유로 응답 도착.
     *  - 페이로드는 Android 가 보낼 때 맞춰야 하는 contract.
     *  - 대다수는 받은 data 를 EventBus 이벤트로 라우팅 (페이지 구독자가 처리).
     * ============================================================================= */

    /**
     * 안드로이드에서 호출하는 콜백함수
     *
     * action 디스패치 규칙:
     * - 점(.)이 포함되어 있으면 window 에서 점경로를 따라 함수 탐색
     *   (예: '$j.F.App.callbackUpload')
     * - 점이 없으면 이 Android 인스턴스의 멤버함수에서 탐색
     *   (예: 'deeplink' → this.deeplink)
     *
     * @param action 함수 이름(멤버) 또는 점경로 문자열
     * @param rawData json객체를 string 으로 변환한 문자열
     */
    callbackBroker(action, rawData) {
        // logcat 에서 grep 가능하도록 단일 문자열로 결합. rawData 는 이미 JSON 문자열이라 그대로 첨부.
        console.log(`[Android.callbackBroker] ${action} ${rawData}`);

        // 모든 수신이 여기를 지난다 — 디버깅 로그도 이 한 곳에서 (§3 앱 자체 트리거 포함)
        this._dbgRecv(action, rawData);

        let payload;
        try {
            payload = JSON.parse(rawData);
        } catch (e) {
            console.error('callbackBroker: JSON parse 실패', { action, rawData, e });
            return;
        }

        const isDotPath = action.includes('.');
        const fn = isDotPath
            ? action.split('.').reduce((obj, key) => obj?.[key], window)
            : this[action];

        if (typeof fn !== 'function') {
            const label = isDotPath ? action : `this.${action}`;
            console.error(`callbackBroker: ${label} 는 함수가 아닙니다.`);
            return;
        }

        if (isDotPath) {
            fn(payload);
        } else {
            fn.call(this, payload);
        }
    }

    /**
     * 연락처 팝업에서 전화번호 선택 후 — openAddressBook 의 응답
     * (※ EventBus 미사용 — 직접 ModalPhone.selectPhone 호출)
     *
     * @param {Object} data
     * @param {string} data.phone - 전화번호 (예: "010-1234-5678")
     * @param {string} data.name  - 연락처에 저장된 이름
     */
    callbackAddressBook(data) {
        $j.F.ModalPhone.selectPhone(data.phone, data.name);
    }

    /**
     * 이미지 파일 업로드 완료 — startUploadImage 의 응답
     * 발행 이벤트: 'upload:image'  (앱·웹 공용 추상 이벤트)
     *
     * @param {Object|Array<Object>} data 단일 객체 또는 배열
     * @param {number} data[].id    - g5_file PK
     * @param {string} data[].path  - 파일 경로 (상대)
     * @param {string} data[].host  - 파일 호스트 키
     * @param {string} data[].type  - 'I'(image) 고정
     *
     * ⚠️ 실패 신호(`{result:false, message}`)가 오면 여기서 공용 토스트를 띄운다 — 호출부마다
     * 실패 처리를 따로 안 둬도 최소한의 안내는 보이게 하려는 것. 기존 성공 페이로드(파일 객체/배열)
     * 는 `result` 필드가 없어 이 분기를 안 타므로 하위호환에 영향 없다. 그래도 EventBus 는 그대로
     * 흘려보낸다 — 호출부가 실패를 직접 처리하고 싶으면 `payload.result === false` 로 가릴 수 있다.
     * (현재 앱은 업로드 실패 시 콜백 자체를 안 부를 수 있어— 이 분기가 실제로 타려면 앱이 실패를
     * {result:false,message} 형태로 회신하도록 먼저 바뀌어야 한다. file-upload-protocol.md 참고.)
     */
    callbackUploadImage(data) {
        if (data && data.result === false) {
            $j.Common.Util.tsalert('error', data.message || '업로드에 실패했습니다.');
        }
        $j.Common.EventBus.trigger('upload:image', data);
    }

    /**
     * 디바이스 사진 한 페이지 도착 — listPhotos 의 응답
     * 발행 이벤트: 'app:photos'
     *
     * @param {Object} data
     * @param {string} data.status      - 'ok' | 'denied' | 'empty'
     * @param {string} data.permission  - 'full' | 'partial' | 'denied'
     * @param {Array<Object>} data.items       - 사진 목록
     * @param {string} data.items[].token      - 앱 소유 불투명 식별자 (업로드 시 그대로 되돌려준다)
     * @param {string} data.items[].thumb      - 썸네일 base64 data URI ('data:image/jpeg;base64,...')
     * @param {string} [data.items[].date]     - 촬영일시
     * @param {string} [data.items[].gps]      - '위도,경도'
     * @param {string|null} data.cursor  - 다음 페이지 커서 (없으면 null)
     * @param {boolean} data.hasMore     - 다음 페이지 존재 여부
     */
    callbackPhotos(data) {
        $j.Common.EventBus.trigger('app:photos', data);
    }

    /**
     * 격자 사진 확대 회신 — previewPhoto 의 응답. 발행 이벤트: 'app:photo:preview'
     * @param {Object} data {token, url?, w?, h?, error?}
     */
    callbackPhotoPreview(data) {
        $j.Common.EventBus.trigger('app:photo:preview', data);
    }

    /**
     * 소셜 인증 결과 — runSocial 의 응답 (구 WebView.applySocialInfo)
     * 발행 이벤트: 'app:social:result'
     *
     * 호출: runSocial 액션의 응답으로 네이버/카카오 인증 완료 시 발화.
     *
     * @param {Object} data
     * @param {number} data.res      - 1=성공, 0=실패
     * @param {string} data.token    - 액세스 토큰
     * @param {string} data.sns_type - 'N'=네이버, 'K'=카카오
     */
    callbackSocialResult(data) {
        $j.Common.EventBus.trigger('app:social:result', data);
    }

    /**
     * STT 인식 결과 — listenStartVoice 의 응답 (구 get_speechWord)
     * 발행 이벤트: 'app:speech:result'
     *
     * 호출: listenStartVoice 액션의 응답으로 STT 인식 후 발화.
     *
     * @param {Object} data
     * @param {string} data.howinput - 입력 모드 ("directinput" 등)
     * @param {string} data.jobAct   - 결과를 채울 대상 element id 또는 action key (예: "listMemoregistext")
     * @param {string} data.word     - STT 인식된 텍스트
     */
    callbackSpeechResult(data) {
        $j.Common.EventBus.trigger('app:speech:result', data);
    }

    /**
     * 폰번호 broker 콜백 — getPhoneNumber 의 응답
     * 발행 이벤트: 'app:phone:number'
     *
     * @param {Object} data
     * @param {string} data.pno     - 디바이스 USIM의 전화번호 ("01012345678")
     * @param {string} [data.uToken] - 디바이스 식별 토큰 (회원가입 등에서 사용)
     */
    callbackPhoneNumber(data) {
        $j.Common.EventBus.trigger('app:phone:number', data);
    }

    /**
     * 디바이스 컨텍스트 broker 콜백 — getDeviceContext 의 응답
     * 발행 이벤트: 'app:device:context'
     *
     * 주의: getDeviceContext 의 응답뿐 아니라 GPS 변화 시 Android 가 자체 트리거로
     *       호출할 수도 있다 (실질적으로 §3 푸시형 성격도 함께 가짐).
     *
     * @param {Object} data
     * @param {string} data.pos1      - 위도 (latitude, "36.40" 형식)
     * @param {string} data.pos2      - 경도 (longitude, "129.01" 형식)
     * @param {string} data.token     - FCM 푸시 토큰
     * @param {string} data.mobie_ver - 앱 버전 ("3.186" 등)
     * @param {string} data.adr       - 역지오코딩된 주소 (이스케이프 처리됨)
     * @param {number|string} data.mb_no  - 회원번호. **웹은 쓰지 않는다** (세션 MEMBER 가 정본.
     *                                  앱은 syncSettings 이전이면 0 을 준다)
     * @param {number} data.isGPS     - GPS 정확도/타입 (0=없음, 1=GPS, 2=네트워크)
     * @param {string} data.modelName - 디바이스 모델 ("Samsung | SM-G998N | 14 | UPSIDE_DOWN_CAKE")
     * @param {string} [data.platform] - 'ios' (iOS 앱만 싣는다. 없으면 안드로이드)
     */
    callbackDeviceContext(data) {
        $j.Common.EventBus.trigger('app:device:context', data);
    }

    /**
     * GPS 권한 요청 결과 — requestLocationPermission 의 응답
     * 발행 이벤트: 'app:location:permission'
     *
     * 사용자가 시스템 권한 다이얼로그에서 선택을 마친 뒤 1회 발화.
     * (권한 상태를 즉시 조회만 하려면 콜백 없는 getLocationPermission() 사용)
     *
     * @param {Object} data
     * @param {number} data.granted - 1=허용, 0=거부
     */
    callbackLocationPermission(data) {
        $j.Common.EventBus.trigger('app:location:permission', data);
    }

    /**
     * 알림 채널 개수 초과 팝업 응답 — setPopUpConfig 의 응답
     * (※ EventBus 미사용 — 콘솔 로그만)
     *
     * @param {Object} data
     * @param {number} data.currentCount - 현재 등록된 알림 채널 수
     * @param {number} data.maxCount     - 시스템 허용 최대 개수
     * @param {string|number} data.userNumb - 회원번호
     */
    callbackChannelCount(data) {
        console.log("callbackChannelCount");
        console.log(data.currentCount);
        console.log(data.maxCount);
        console.log(data.userNumb);
    }

    /* =============================================================================
     *  3. App → Web  ─  앱 주도 푸시형 (요청-응답이 아닌 자체 트리거)
     * =============================================================================
     *  - Android 가 시스템 이벤트(딥링크 진입, 키보드 변화, 연락처 동기화 등) 를
     *    자체적으로 감지해 웹에 통보. 웹이 먼저 부른 게 아님.
     *  - 진입은 §2 의 callbackBroker 와 동일하지만 의미는 다르므로 분리.
     *  - 페이지 구독자는 EventBus 로 받아 자체 갱신.
     * ============================================================================= */

    /**
     * 딥링크 진입 — code 에 매핑된 페이지로 이동.
     * 안드로이드는 callbackBroker(action='deeplink', rawData) 로 호출.
     * (멤버 디스패치 — 점 없는 'deeplink' 액션이 this.deeplink 로 라우팅)
     *
     * code → 페이지 매핑은 서버 AppRegistry pages 한 곳에서 관리(G5.APPS). 매핑 없는 코드는 그 앱 홈으로 이동.
     * 백버튼 대응으로 location.replace 사용 (CLAUDE.md 페이지 이동 규칙).
     *
     * @param {Object|string} data {code:'002', app:'JB'} 객체 또는 코드 문자열
     * @param {string} [data.code] 페이지 코드 (페이지코드표 키, 3자리 zero-pad)
     * @param {string} [data.app]  대상 앱 코드. 미지정 시 현재 경로로 자동 판별.
     */
    deeplink(data) {
        // 코드 추출 — 객체({code}) 또는 코드 문자열 양쪽 허용, 3자리 zero-pad 정규화
        const raw = (data && typeof data === 'object') ? data.code : data;
        const code = String(raw).padStart(3, '0');
        // 앱 분기 — 딥링크가 app 을 함께 전달. 미지정이면 경로 기반 폴백.
        const app = (data && typeof data === 'object') ? data.app : null;

        const { list, home } = this._contextByApp(app);   // app 우선, 미지정 시 경로로 분기
        const url = list[code];
        if (!url) {
            console.warn(`[Android.deeplink] 매핑 없는 코드: ${JSON.stringify(data)} → 홈 이동(${home})`);
            location.replace(home);
            return;
        }
        location.replace(url);
    }

    /**
     * 악덕업체보기 — 앱이 악덕마스터번호를 전달해 상세 BS 표시를 요청.
     * 발행 이벤트: 'app:devil:master'
     *
     * 호출: 악덕알리미 앱이 '악덕업체보기' 딥링크/네이티브 액션을 자체 발화.
     * 구독: Sites/Devil/Bootstrap 이 모든 /b 페이지 공통으로 받아 마스터 상세 BS 표시.
     *
     * @param {Object} data
     * @param {number} data.master_num - 악덕마스터번호 (악덕 master PK)
     */
    devilMaster(data) {
        $j.Common.EventBus.trigger('app:devil:master', data);
    }

}
